import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import LanguageToggle from "../components/LanguageToggle";
import {
  ArrowLeft, ArrowRight, Loader2, Check, Sparkles, Eye, EyeOff,
} from "lucide-react";
import { BUSINESS_CATEGORIES } from "../lib/businessTypes";

const PLANS = {
  basico: { id: "basico", name: "Básico", price: 29 },
  pro: { id: "pro", name: "Pro", price: 59 },
  ai: { id: "ai", name: "AI", price: 100 },
};

export default function SignupPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const rawPlan = (params.get("plan") || "pro").toLowerCase();
  const isCustomFlow = rawPlan === "custom";
  // Custom AI flow — pull the Sofia-quoted price + business context from the URL
  const customPrice = (() => {
    if (!isCustomFlow) return null;
    const n = parseInt(params.get("price") || "150", 10);
    if (!Number.isFinite(n)) return 150;
    return Math.max(100, Math.min(200, n));
  })();
  const customModules = isCustomFlow ? (params.get("modules") || "").split(",").filter(Boolean) : [];
  const customSummary = isCustomFlow ? (params.get("summary") || "") : "";
  const customBusinessType = isCustomFlow ? (params.get("type") || "") : "";

  // Build the effective plans table the picker uses. Custom flow shows ONLY
  // the custom card with the quoted price; the visitor cannot accidentally
  // downgrade to Basic/Pro/AI on the way to checkout.
  const PLANS_MAP = isCustomFlow
    ? { custom: { id: "custom", name: "Commandix Custom AI", price: customPrice } }
    : PLANS;
  const initialPlan = isCustomFlow ? "custom" : (PLANS[rawPlan] ? rawPlan : "pro");

  const [step, setStep] = useState(1);
  const [plan, setPlan] = useState(initialPlan);
  const [businessType, setBusinessType] = useState(customBusinessType);
  const [businessName, setBusinessName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async () => {
    if (!businessName.trim() || !ownerName.trim() || !email.trim() || password.length < 6 || !businessType) {
      setError(t("signup.error_required"));
      return;
    }
    setLoading(true); setError("");
    try {
      const body = {
        business_name: businessName.trim(),
        business_type: businessType,
        owner_name: ownerName.trim(),
        owner_email: email.trim().toLowerCase(),
        owner_phone: phone.trim(),
        password,
        plan,
      };
      if (plan === "custom") {
        body.custom_price = customPrice;
        body.custom_modules = customModules;
        body.custom_summary = customSummary;
      }
      await api.post("/saas/signup", body);
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Error al crear cuenta");
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-12" style={{ fontFamily: "'Work Sans', sans-serif" }}>
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-[#FF7A00]/10 border-2 border-[#FF7A00] mx-auto flex items-center justify-center mb-6">
            <Check className="text-[#FF7A00]" size={40} strokeWidth={3} />
          </div>
          <h1 className="text-4xl font-extrabold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("signup.success_title")}</h1>
          <p className="text-zinc-400 mb-2">
            {t("signup.success_msg_1")} <span className="text-white font-semibold">{email}</span>.
          </p>
          <p className="text-zinc-500 text-sm mb-8">
            {t("signup.success_msg_2")}
          </p>
          <Button
            onClick={() => navigate("/login")}
            className="bg-[#FF7A00] hover:bg-[#FF8E1F] text-black font-bold w-full h-12"
            data-testid="signup-go-login"
          >
            {t("signup.success_cta")} <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" style={{ fontFamily: "'Work Sans', sans-serif" }} data-testid="signup-page">
      {/* Subtle glow */}
      <div className="fixed inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 50% at 50% 0%, rgba(255,122,0,0.08), transparent 80%)" }} />

      <header className="relative z-10 px-6 md:px-12 h-16 flex items-center justify-between border-b border-zinc-900">
        <Link to="/" className="flex items-center" data-testid="signup-back-home">
          <img src="/brand/commandix-logo-tight.png" alt="Commandix" className="h-9 md:h-10 object-contain" draggable="false" />
        </Link>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <Link to="/login" className="text-sm text-zinc-400 hover:text-white" data-testid="signup-login-link">
            {t("signup.have_account")} <span className="text-[#FF7A00] font-semibold">{t("signup.have_account_link")}</span>
          </Link>
        </div>
      </header>

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-12 md:py-16">
        {/* Progress */}
        <div className="flex items-center gap-2 mb-8 text-xs text-zinc-500">
          <span className={step >= 1 ? "text-[#FF7A00] font-bold" : ""}>1. {t("signup.step_1_title")}</span>
          <span>—</span>
          <span className={step >= 2 ? "text-[#FF7A00] font-bold" : ""}>2. {t("signup.step_2_title")}</span>
          <span>—</span>
          <span className={step >= 3 ? "text-[#FF7A00] font-bold" : ""}>3. {t("signup.step_3_title")}</span>
        </div>

        {step === 1 && (
          <div data-testid="signup-step-1">
            <Badge className="bg-[#FF7A00]/10 text-[#FF7A00] border border-[#FF7A00]/20 mb-3 uppercase tracking-[0.2em] text-[10px]">
              <Sparkles size={11} className="mr-1" /> {t("signup.step_progress", { n: 1 })}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("signup.step_1_title")}</h1>
            <p className="text-zinc-400 mb-8">{t("signup.step_1_subtitle")}</p>

            <div className={`grid gap-3 mb-8 ${isCustomFlow ? "max-w-md mx-auto" : "sm:grid-cols-3"}`}>
              {Object.values(PLANS_MAP).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlan(p.id)}
                  className={`p-5 rounded-xl text-left border-2 transition ${
                    plan === p.id
                      ? "border-[#FF7A00] bg-[#FF7A00]/10 shadow-[0_0_30px_rgba(255,122,0,0.2)]"
                      : "border-zinc-800 bg-zinc-950 hover:border-zinc-600"
                  }`}
                  data-testid={`signup-plan-${p.id}`}
                >
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1">{p.name}</p>
                  <p className="text-3xl font-extrabold" style={{ fontFamily: "'Outfit', sans-serif" }}>${p.price}</p>
                  <p className="text-xs text-zinc-500 mt-1">{t("pricing.month")}</p>
                  {p.id === "custom" && customSummary && (
                    <p className="text-[11px] text-zinc-400 mt-2 leading-snug italic">{customSummary}</p>
                  )}
                  {p.id === "custom" && customModules.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {customModules.slice(0, 5).map((m, i) => (
                        <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-[#FF7A00]/10 text-[#FFB266] border border-[#FF7A00]/20 uppercase tracking-wide">
                          {m}
                        </span>
                      ))}
                      {customModules.length > 5 && (
                        <span className="text-[9px] text-zinc-500">+{customModules.length - 5}</span>
                      )}
                    </div>
                  )}
                </button>
              ))}
            </div>

            <Button
              onClick={() => setStep(2)}
              className="w-full bg-[#FF7A00] hover:bg-[#FF8E1F] text-black font-bold h-12"
              data-testid="signup-next-1"
            >
              {t("signup.continue")} <ArrowRight size={18} className="ml-2" />
            </Button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="signup-step-2">
            <button onClick={() => setStep(1)} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4" data-testid="signup-back-1">
              <ArrowLeft size={14} /> {t("signup.back")}
            </button>
            <Badge className="bg-[#FF7A00]/10 text-[#FF7A00] border border-[#FF7A00]/20 mb-3 uppercase tracking-[0.2em] text-[10px]">
              {t("signup.step_progress", { n: 2 })}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("signup.step_2_title")}</h1>
            <p className="text-zinc-400 mb-8">{t("signup.step_2_subtitle")}</p>

            <div className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-bold">{t("signup.business_name")}</Label>
                <Input
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder={t("signup.business_name_ph")}
                  className="bg-transparent border-x-0 border-t-0 border-b border-zinc-800 focus:border-[#FF7A00] rounded-none h-12 text-base px-0 mt-1"
                  data-testid="signup-business-name"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-bold mb-2 block">{t("signup.business_type")}</Label>
                <div className="bg-zinc-950 border border-zinc-800 rounded-xl max-h-[340px] overflow-y-auto p-2">
                  {BUSINESS_CATEGORIES.map((cat) => (
                    <div key={cat.label} className="mb-3 last:mb-0">
                      <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
                        {cat.icon} {cat.label}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {cat.types.map((t) => (
                          <button
                            key={t.value}
                            type="button"
                            onClick={() => setBusinessType(t.value)}
                            className={`px-3 py-2 rounded-lg text-sm text-left transition ${
                              businessType === t.value
                                ? "bg-[#FF7A00] text-black font-bold"
                                : "hover:bg-white/5 text-zinc-300"
                            }`}
                            data-testid={`signup-bt-${t.value}`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <Button
              onClick={() => {
                if (!businessName.trim() || !businessType) {
                  setError(t("signup.error_business"));
                  return;
                }
                setError("");
                setStep(3);
              }}
              disabled={!businessName.trim() || !businessType}
              className="w-full bg-[#FF7A00] hover:bg-[#FF8E1F] text-black font-bold h-12 mt-8 disabled:opacity-50 disabled:hover:bg-[#FF7A00]"
              data-testid="signup-next-2"
            >
              {t("signup.continue")} <ArrowRight size={18} className="ml-2" />
            </Button>
            {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
          </div>
        )}

        {step === 3 && (
          <div data-testid="signup-step-3">
            <button onClick={() => setStep(2)} className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white mb-4" data-testid="signup-back-2">
              <ArrowLeft size={14} /> {t("signup.back")}
            </button>
            <Badge className="bg-[#FF7A00]/10 text-[#FF7A00] border border-[#FF7A00]/20 mb-3 uppercase tracking-[0.2em] text-[10px]">
              {t("signup.step_progress", { n: 3 })}
            </Badge>
            <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t("signup.step_3_title")}</h1>
            <p className="text-zinc-400 mb-8">{t("signup.step_3_subtitle")}</p>

            <div className="space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-bold">{t("signup.owner_name")}</Label>
                <Input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} placeholder="Juan Pérez" className="bg-transparent border-x-0 border-t-0 border-b border-zinc-800 focus:border-[#FF7A00] rounded-none h-12 text-base px-0 mt-1" data-testid="signup-owner-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-bold">{t("signup.email")}</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" className="bg-transparent border-x-0 border-t-0 border-b border-zinc-800 focus:border-[#FF7A00] rounded-none h-12 text-base px-0 mt-1" data-testid="signup-email" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-bold">{t("signup.phone")}</Label>
                <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 809 555 5555" className="bg-transparent border-x-0 border-t-0 border-b border-zinc-800 focus:border-[#FF7A00] rounded-none h-12 text-base px-0 mt-1" data-testid="signup-phone" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-bold">{t("signup.password")}</Label>
                <div className="relative">
                  <Input type={showSignupPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-transparent border-x-0 border-t-0 border-b border-zinc-800 focus:border-[#FF7A00] rounded-none h-12 text-base px-0 pr-10 mt-1" data-testid="signup-password" />
                  <button
                    type="button"
                    onClick={() => setShowSignupPassword(v => !v)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 mt-0.5"
                    aria-label={showSignupPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    data-testid="signup-password-toggle"
                  >
                    {showSignupPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 rounded-xl bg-zinc-950 border border-zinc-900 text-sm">
              <p className="text-zinc-400">
                {t("signup.plan_summary")} <span className="text-white font-bold">{PLANS_MAP[plan]?.name}</span> · <span className="text-[#FF7A00] font-bold">${PLANS_MAP[plan]?.price}{t("pricing.month")}</span>
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                {t("signup.trial_note")}
              </p>
            </div>

            <Button
              onClick={submit}
              disabled={loading}
              className="w-full bg-[#FF7A00] hover:bg-[#FF8E1F] text-black font-bold h-14 mt-6 text-base shadow-[0_0_40px_rgba(255,122,0,0.3)]"
              data-testid="signup-submit"
            >
              {loading ? <><Loader2 size={18} className="animate-spin mr-2" /> {t("signup.submitting")}</> : <>{t("signup.submit")} <ArrowRight size={18} className="ml-2" /></>}
            </Button>

            {error && <p className="text-red-400 text-sm mt-3" data-testid="signup-error">{error}</p>}

            <p className="text-xs text-zinc-500 mt-4 text-center">
              {t("signup.terms_note")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
